import { createPortal } from 'react-dom';
import { X, Building2, LayoutGrid, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';

export type OrgUnitModalKind = 'department' | 'sector';

export type OrgUnitPickerItem = {
	id: string;
	name: string;
	code: string;
	description?: string;
	/** Ex.: departamento pai (setores) */
	secondaryLine?: string;
};

const COPY: Record<
	OrgUnitModalKind,
	{ title: string; emptyTitle: string; emptyBody: string }
> = {
	department: {
		title: 'Vincular departamento à empresa',
		emptyTitle: 'Nenhum departamento disponível',
		emptyBody:
			'Todos os departamentos já estão vinculados ou o catálogo está vazio.',
	},
	sector: {
		title: 'Vincular setor à empresa',
		emptyTitle: 'Nenhum setor disponível',
		emptyBody:
			'Todos os setores já estão vinculados ou o catálogo está vazio.',
	},
};

type LinkOrgUnitModalProps = {
	isOpen: boolean;
	onClose: () => void;
	kind: OrgUnitModalKind;
	catalog: OrgUnitPickerItem[];
	linkedIds: string[];
	onLink: (id: string) => void | Promise<void>;
	isLinking?: boolean;
};

export const LinkOrgUnitModal = ({
	isOpen,
	onClose,
	kind,
	catalog,
	linkedIds,
	onLink,
	isLinking = false,
}: LinkOrgUnitModalProps) => {
	if (!isOpen) return null;

	const meta = COPY[kind];
	const Icon = kind === 'department' ? Building2 : LayoutGrid;
	const linked = new Set(linkedIds);
	const available = catalog.filter((c) => !linked.has(c.id));

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-4 py-4 sm:px-8 sm:py-6">
					<h2 className="text-lg font-black text-stone-900">
						{meta.title}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto p-4 sm:p-8">
					{available.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-8 text-center sm:py-12">
							<div className="mb-3 rounded-full bg-stone-100 p-4">
								<Icon size={28} className="text-stone-400" />
							</div>
							<p className="text-sm font-bold text-stone-600">
								{meta.emptyTitle}
							</p>
							<p className="mt-1 max-w-sm text-xs text-stone-400">
								{meta.emptyBody}
							</p>
						</div>
					) : (
						<div className="space-y-2">
							{available.map((item) => (
								<div
									key={item.id}
									className="group flex flex-col gap-3 rounded-2xl border border-stone-100 bg-stone-50/50 p-4 transition-all hover:border-stone-200 hover:bg-white sm:flex-row sm:items-center sm:justify-between sm:gap-4"
								>
									<div className="flex min-w-0 flex-1 items-start gap-3">
										<div className="mt-0.5 rounded-lg bg-stone-100 p-2">
											<Icon
												size={18}
												className="text-stone-500"
											/>
										</div>
										<div className="min-w-0 flex-1 space-y-1">
											<div className="flex flex-wrap items-center gap-2">
												<span className="text-sm font-bold text-stone-900">
													{item.name}
												</span>
												<span className="rounded-md border border-stone-200 bg-white px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-stone-500">
													{item.code}
												</span>
											</div>
											{item.secondaryLine ? (
												<p className="text-[10px] font-bold uppercase tracking-tight text-stone-400">
													{item.secondaryLine}
												</p>
											) : null}
											{item.description ? (
												<p className="text-xs leading-snug text-stone-500">
													{item.description}
												</p>
											) : null}
										</div>
									</div>
									<Button
										text={
											isLinking
												? 'Vinculando...'
												: 'Vincular'
										}
										disabled={isLinking}
										className="w-full shrink-0 rounded-full bg-black px-4 py-2 text-xs font-bold text-white shadow-md hover:bg-neutral-800 disabled:opacity-70 sm:w-auto"
										startIcon={
											isLinking ? (
												<Loader2 className="size-3.5 animate-spin" />
											) : (
												<Check className="size-3.5 stroke-[3]" />
											)
										}
										onClick={() => void onLink(item.id)}
									/>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
